// modules/website/components/TrophyGalleryComponent.tsx - Galeria de Troféus de Pesca

import React, { useState } from 'react';
import { TrophyGalleryData } from '@/types';
import { Fish, Award, MapPin, User, Calendar, Scale } from 'lucide-react';

interface Props {
  data: TrophyGalleryData;
  settings?: any;
}

export const TrophyGalleryComponent: React.FC<Props> = ({ data, settings }) => {
  const {
    title = 'Galeria de Troféus',
    subtitle,
    images = [],
    layout = 'grid',
    columns = 3,
    showFilters = false,
    filterBySpecies = false
  } = data || {};

  const [selectedSpecies, setSelectedSpecies] = useState<string | null>(null);
  const [lightboxImage, setLightboxImage] = useState<number | null>(null);

  // Extrair espécies únicas para filtro
  const uniqueSpecies = [...new Set(images.map(img => img.fishSpecies).filter(Boolean))];

  // Filtrar imagens
  const filteredImages = selectedSpecies
    ? images.filter(img => img.fishSpecies === selectedSpecies)
    : images;

  // Classes de grid por colunas
  const gridClasses = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
  };

  return (
    <div 
      className="py-16 px-6"
      style={{ backgroundColor: settings?.backgroundColor || '#f9fafb' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-4">
            <Award className="text-yellow-500" size={32} />
            <h2 className="text-4xl font-bold text-gray-900">{title}</h2>
          </div>
          {subtitle && (
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{subtitle}</p>
          )}
        </div>

        {/* Filtros */}
        {showFilters && filterBySpecies && uniqueSpecies.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <button
              onClick={() => setSelectedSpecies(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                !selectedSpecies
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Todas ({images.length})
            </button>
            {uniqueSpecies.map(species => (
              <button
                key={species}
                onClick={() => setSelectedSpecies(species)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedSpecies === species
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {species} ({images.filter(i => i.fishSpecies === species).length})
              </button>
            ))}
          </div>
        )}

        {/* Gallery Grid */}
        <div className={`grid ${gridClasses[columns]} gap-6`}>
          {filteredImages.map((image, index: number) => (
            <div
              key={image.id || index}
              className="group relative bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              onClick={() => setLightboxImage(index)}
            >
              {/* Imagem */}
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={image.src || '/placeholder-fish.jpg'}
                  alt={image.alt || `${image.fishSpecies} - ${image.anglerName}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>

              {/* Badge de Destaque */}
              {image.isFeatured && (
                <div className="absolute top-3 right-3 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <Award size={12} />
                  Destaque
                </div>
              )}

              {/* Overlay com Informações */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
                {/* Espécie */}
                <div className="flex items-center gap-2 text-white mb-1">
                  <Fish size={18} className="text-blue-400" />
                  <span className="font-bold text-lg">{image.fishSpecies}</span>
                </div>

                {/* Peso e Tamanho */}
                {(image.weight || image.length) && (
                  <div className="flex items-center gap-3 text-gray-200 text-sm mb-1">
                    {image.weight && (
                      <span className="flex items-center gap-1">
                        <Scale size={14} />
                        {image.weight} kg
                      </span>
                    )}
                    {image.length && (
                      <span>📏 {image.length} cm</span>
                    )}
                  </div>
                )}

                {/* Pescador */}
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <User size={14} />
                  <span>{image.anglerName}</span>
                </div>
              </div>

              {/* Info Expandida no Hover */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                <div className="text-white space-y-2">
                  <div className="flex items-center gap-2">
                    <Fish size={20} className="text-blue-400" />
                    <span className="font-bold text-xl">{image.fishSpecies}</span>
                  </div>
                  
                  {(image.weight || image.length) && (
                    <div className="flex gap-4 text-sm">
                      {image.weight && <span>⚖️ {image.weight} kg</span>}
                      {image.length && <span>📏 {image.length} cm</span>}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-sm">
                    <User size={14} />
                    <span>{image.anglerName}</span>
                  </div>
                  
                  {image.location && (
                    <div className="flex items-center gap-2 text-sm text-gray-300">
                      <MapPin size={14} />
                      <span>{image.location}</span>
                    </div>
                  )}
                  
                  {image.date && (
                    <div className="flex items-center gap-2 text-sm text-gray-300">
                      <Calendar size={14} />
                      <span>{image.date}</span>
                    </div>
                  )}
                  
                  {image.guideName && (
                    <div className="text-sm text-gray-400">
                      Guia: {image.guideName}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredImages.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <Fish size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg">Nenhum troféu encontrado</p>
          </div>
        )}

        {/* Lightbox */}
        {lightboxImage !== null && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setLightboxImage(null)}
          >
            <button 
              className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300"
              onClick={() => setLightboxImage(null)}
            >
              ×
            </button>
            <img
              src={filteredImages[lightboxImage]?.src}
              alt={filteredImages[lightboxImage]?.alt || ''}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        )}
      </div>
    </div>
  );
};
